import type { ResolvedBranding } from '@lkq/shared';

// Per-host branding cache (localStorage). The app is a static SPA, so the concept
// theme can only be resolved client-side via the async /concept-config fetch —
// which means the first paint would otherwise show the platform-default theme
// until that request returns (a visible ~1s flash on slow networks, especially
// when switching between concept hosts / on login pages). Caching each host's
// last-known branding lets us re-apply it synchronously at boot, so a revisited
// host is on-theme from the first paint. The cache self-heals: the live fetch
// overwrites it when branding changes. Keyed by hostname; localStorage is
// per-origin, so concept hosts never read each other's themes.

const key = (host: string) => `lkq.branding.${host}`;

export function readCachedBranding(host: string): ResolvedBranding | null {
  try {
    const raw = localStorage.getItem(key(host));
    return raw ? (JSON.parse(raw) as ResolvedBranding) : null;
  } catch {
    return null; // private mode / disabled storage / malformed JSON — fall back to default
  }
}

export function writeCachedBranding(host: string, branding: ResolvedBranding): void {
  try {
    localStorage.setItem(key(host), JSON.stringify(branding));
  } catch {
    /* quota / disabled storage — caching is a best-effort optimisation */
  }
}
